/**
 * Data layer for `cap-utilization-widget`. Thin typed fetch wrappers over /api/v1/cap-utilization-widget.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface CapUtilizationWidgetRow {
	id: number;
	tenant_id: string;
	scope_type: string;
	scope_id: string;
	cap_type: string;
	cap_value: number;
	used_value: number;
	period_start: number;
	period_end: number;
	utilization_pct: number | null;
	created_at: number;
	updated_at: number;
}

export interface ListCapUtilizationWidgetResponse {
	items: CapUtilizationWidgetRow[];
}

export interface CreateCapUtilizationWidgetBody {
	scope_type: string;
	scope_id: string;
	cap_type: string;
	cap_value: number;
	used_value?: number;
	period_start: number;
	period_end: number;
}

export interface ListCapUtilizationWidgetParams {
	sort?: "utilization" | "created_at";
	scope_type?: string;
	cap_type?: string;
}

const BASE = "/api/v1/cap-utilization-widget";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(
	params?: ListCapUtilizationWidgetParams,
): Promise<ListCapUtilizationWidgetResponse> {
	const qs = new URLSearchParams();
	if (params?.sort) qs.set("sort", params.sort);
	if (params?.scope_type) qs.set("scope_type", params.scope_type);
	if (params?.cap_type) qs.set("cap_type", params.cap_type);
	const url = qs.toString() ? `${BASE}?${qs.toString()}` : BASE;
	const res = await fetch(url, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCapUtilizationWidgetResponse>(res);
}

export async function create(
	body: CreateCapUtilizationWidgetBody,
): Promise<CapUtilizationWidgetRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<CapUtilizationWidgetRow>(res);
}

export const capUtilizationWidgetQueryKeys = {
	all: ["cap-utilization-widget"] as const,
	list: (params?: ListCapUtilizationWidgetParams) =>
		[...capUtilizationWidgetQueryKeys.all, "list", params ?? {}] as const,
	detail: (id: string | number) => [...capUtilizationWidgetQueryKeys.all, id] as const,
};
